import React, { useEffect, useState } from 'react';
import './Landing.css';

// Shown until /projects answers, and kept as the fallback if it can't be
// reached -- the showcase degrades to this list rather than to an empty
// section. Once a domain fills in its project copy in /admin, the live data
// replaces this wholesale.
const FALLBACK_PROJECTS = [
  {
    name: 'SweetPost',
    url: 'https://sweetpost.art',
    description: 'Create postcards from museum art collections.',
  },
  {
    name: 'Who is Simon Tashimoto?',
    url: 'https://tashimoto.vaguespac.es/',
    description: 'Generative prints inspired by the work of Simon Tashimoto.',
  },
  {
    name: 'Habsburg Frogs',
    url: 'https://frogs.vaguespac.es/',
    description: 'An AI benchmark for testing models against art history.',
  },
];

export default function Landing() {
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);

  useEffect(() => {
    let live = true;
    fetch(process.env.REACT_APP_BACKEND_URL + '/projects')
      .then((res) => res.json())
      .then((json) => {
        if (!live || json.result !== 'success' || !json.projects.length) return;
        setProjects(json.projects.map((p) => ({
          name: p.title,
          url: p.url,
          description: p.description,
          images: p.images || [],
        })));
      })
      .catch(() => { /* keep the fallback list */ });
    return () => { live = false; };
  }, []);

  return (
    <div className="App landing">
      <div className="landing-section landing-hero">
        <h1>Ollie Mail</h1>
        <p className="landing-tagline">Enjoy getting mail again!</p>
        <p className="description">
          Ollie Mail is a drop-in service that lets you easily mail a physical postcard of an image of your choice.
        </p>
        <p className="description">
          Any artist, museum or gallery can easily begin to monetize their work or collection with Ollie Mail. If you are interested in joining our service you can reach us at <a className="backlink" href="mailto:support@olliemail.net">support@olliemail.net</a>.
        </p>
      </div>

      <div className="landing-section">
        <h2>How it works</h2>
        <ol className="landing-steps">
          <li>You embed a small JS snippet on your site, alongside a <code>&lt;canvas&gt;</code> and a button.</li>
          <li>A visitor generates artwork however your project works.</li>
          <li>They click your button and land on Ollie Mail with their image attached.</li>
          <li>They enter a recipient address, pay, and we mail the postcard within a few days.</li>
        </ol>
      </div>

      <div className="landing-section">
        <h2>Projects on the platform</h2>
        <ul className="landing-projects">
          {projects.map(p => (
            <li key={p.url}>
              <a className="backlink" href={p.url} target="_blank" rel="noreferrer">
                {p.name}
              </a>
              {p.description && <span className="landing-project-desc"> &mdash; {p.description}</span>}
              {p.images && p.images.length > 0 && (
                <div className="landing-project-images">
                  {p.images.map((src) => (
                    <a key={src} href={p.url} target="_blank" rel="noreferrer">
                      <img src={src} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="landing-section">
        <h2>For developers</h2>
        <p className="description">
          Embed reference, canvas sizing for print, merge variable docs, and an interactive setup assistant live in the <a className="backlink" href="/developers">developer portal</a>.
        </p>
      </div>

      <div className="landing-section">
        <h2>Join the platform</h2>
        <p className="description">
          We're always interested in adding new projects. If you'd like to bring Ollie Mail to your site, drop a note to <a className="backlink" href="mailto:support@olliemail.net">support@olliemail.net</a>.
        </p>
      </div>
    </div>
  );
}
